// ./src/utils/api/OrganizerProfileService.ts
//
// Organizer profile service — talks to the eventsmagic-api `organizerProfile`
// entity (ROADMAP A2b #54).
//
// An organizer profile is the account-level PUBLIC identity a host puts their
// events on: name, logo, description, address, contact details, social links, and
// which optional sections their public page (`/o/<slug>`) offers. `slug` is that
// page's address, so it is immutable once the page leaves draft — the server
// enforces that, and the form locks the field to match.
//
// Everything returns an OpResult directly (no wrapper) — read errors at the render
// boundary with the opResultHelpers.
import { gql } from "@apollo/client";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { runBulkWrites } from "../bulkWrites";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const ORGANIZER_PROFILE_FIELDS = `
  id
  accountId
  slug
  name
  summary
  description
  kind
  themeId
  themeOverrides
  logoUploadedFileId
  headerUploadedFileId
  backgroundUploadedFileId
  logoUploadedFile {
    id
    fileUrl
    thumbnailUrl
  }
  headerUploadedFile {
    id
    fileUrl
  }
  placeName
  country
  stateProvince
  city
  postalCode
  streetAddress
  website
  phone
  fax
  email
  socialUrl1
  socialUrl2
  socialUrl3
  isListed
  listingStatus
  enableMemberships
  enableDonations
  enableContactUs
  enableNews
  enableFollowEmail
  followerCount
  membershipsDescription
  donationsDescription
  status
  rights
  createdAt
  updatedAt
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const ORGANIZER_PROFILE_LIST_QUERY = gql`
  query OrganizerProfileList($filter: OrganizerProfileFilter, $params: PaginationAndSorting) {
    organizerProfileList(filter: $filter, params: $params) {
      code
      total
      data {
        ${ORGANIZER_PROFILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ORGANIZER_PROFILE_GET_QUERY = gql`
  query OrganizerProfileGet($id: ID) {
    organizerProfileGet(id: $id) {
      code
      data {
        ${ORGANIZER_PROFILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ORGANIZER_PROFILE_SLUG_QUERY = gql`
  query OrganizerProfileSlugAvailable($slug: String, $organizerProfileId: ID) {
    organizerProfileSlugAvailable(slug: $slug, organizerProfileId: $organizerProfileId) {
      code
      data {
        slug
        available
        reason
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const ORGANIZER_PROFILE_CREATE_MUTATION = gql`
  mutation OrganizerProfileCreate($params: OrganizerProfileInput) {
    organizerProfileCreate(params: $params) {
      code
      data {
        ${ORGANIZER_PROFILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ORGANIZER_PROFILE_UPDATE_MUTATION = gql`
  mutation OrganizerProfileUpdate($where: OrganizerProfileWhereInput, $params: OrganizerProfileInput) {
    organizerProfileUpdate(where: $where, params: $params) {
      code
      data {
        ${ORGANIZER_PROFILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ORGANIZER_PROFILE_REMOVE_MUTATION = gql`
  mutation OrganizerProfileRemove($where: OrganizerProfileWhereInput) {
    organizerProfileRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api organizerProfile schema)
// =============================================================================

/** Public-page lifecycle — mirrors the API's ORGANIZER_LISTING_STATUS boundary. */
export const ORGANIZER_LISTING_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
  DISABLED: "disabled",
} as const;

export type OrganizerListingStatus =
  (typeof ORGANIZER_LISTING_STATUS)[keyof typeof ORGANIZER_LISTING_STATUS];

/** What the profile represents — mirrors the API's ORGANIZER_PROFILE_KIND boundary. */
export const ORGANIZER_PROFILE_KIND = {
  ORGANIZATION: "organization",
  HOUSEHOLD: "household",
  TEAM: "team",
  OTHER: "other",
} as const;

export type OrganizerProfileKind =
  (typeof ORGANIZER_PROFILE_KIND)[keyof typeof ORGANIZER_PROFILE_KIND];

export interface OrganizerProfile {
  id?: string | undefined;
  accountId?: string | undefined;
  slug?: string | undefined;
  name?: string | undefined;
  summary?: string | null | undefined;
  description?: string | null | undefined;
  kind?: string | undefined;
  themeId?: string | null | undefined;
  /** B20 round 2 — the page's adjustments to its theme (look + background pictures), deep-merged over the theme's tokens. */
  themeOverrides?: Record<string, any> | null | undefined;
  logoUploadedFileId?: string | null | undefined;
  headerUploadedFileId?: string | null | undefined;
  backgroundUploadedFileId?: string | null | undefined;
  /** Resolved by ms_storage — read-only, never sent back on save. */
  logoUploadedFile?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  /** Resolved by ms_storage — read-only, never sent back on save. */
  headerUploadedFile?: { id?: string | null; fileUrl?: string | null } | null;
  placeName?: string | null | undefined;
  country?: string | null | undefined;
  stateProvince?: string | null | undefined;
  city?: string | null | undefined;
  postalCode?: string | null | undefined;
  streetAddress?: string | null | undefined;
  website?: string | null | undefined;
  phone?: string | null | undefined;
  fax?: string | null | undefined;
  email?: string | null | undefined;
  socialUrl1?: string | null | undefined;
  socialUrl2?: string | null | undefined;
  socialUrl3?: string | null | undefined;
  isListed?: boolean | undefined;
  listingStatus?: string | undefined;
  enableMemberships?: boolean | undefined;
  enableDonations?: boolean | undefined;
  enableContactUs?: boolean | undefined;
  enableNews?: boolean | undefined;
  /** B24.5 — visitors may follow this page by email (double opt-in); followers hear about each newly published event. */
  enableFollowEmail?: boolean | undefined;
  /** B24.5 — how many follow it by email now (confirmed, not unsubscribed). Read-only. */
  followerCount?: number | null | undefined;
  membershipsDescription?: string | null | undefined;
  donationsDescription?: string | null | undefined;
  status?: number | undefined;
  /** Actions the current caller may perform on this record ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
}

/**
 * ⚠ The resolved `*UploadedFile` objects are OMITTED. They are read-only projections
 * of the id columns; sending one back would be an unknown input field and the whole
 * mutation would be rejected — which, on a save, looks exactly like a server that
 * silently refuses to store anything.
 */
export type OrganizerProfileInput = Omit<
  OrganizerProfile,
  | "id"
  | "accountId"
  | "status"
  | "rights"
  | "createdAt"
  | "updatedAt"
  | "logoUploadedFile"
  | "headerUploadedFile"
  | "slug"
  // B24.5 — read-only (the server counts it); not on the SDL input.
  | "followerCount"
> & {
  /**
   * ⚠ NULLABLE ON INPUT, never null on read. Sending null (or omitting it) on
   * create means "derive a link from the name" — the server does that in
   * `OrganizerProfileCore.beforeCreate`. The column itself is NOT NULL, which is
   * why the two shapes differ.
   */
  slug?: string | null;
};

export interface OrganizerProfileFilter {
  id?: string[] | null | undefined;
  slug?: string[] | null | undefined;
  kind?: string[] | null | undefined;
  listingStatus?: string[] | null | undefined;
  /** Case-insensitive partial match on name + slug. */
  searchKeyword?: string | null | undefined;
}

export interface OrganizerProfileWhereInput {
  id?: string | undefined;
}

/** The answer behind the public-link field's live check. */
export interface OrganizerSlugCheck {
  slug?: string | undefined;
  available?: boolean | undefined;
  /** 'available' | 'taken' | 'invalid' */
  reason?: string | undefined;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Organizer profile service — CRUD over the account's public organizer pages,
 * plus the live slug check the edit form's link field uses.
 */
export class OrganizerProfileService {
  /** List the account's organizer profiles. */
  static async list(
    filter?: OrganizerProfileFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(ORGANIZER_PROFILE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
  }

  /** Get one organizer profile by ID. */
  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(ORGANIZER_PROFILE_GET_QUERY, { id });
  }

  /**
   * Is this public link free?
   *
   * `organizerProfileId` excludes the profile being edited, so re-saving a page
   * without touching its link does not report the page's own slug as taken.
   */
  static async slugAvailable(
    slug: string,
    organizerProfileId?: string | null
  ): Promise<OpResult> {
    return ApiService.executeQuery(ORGANIZER_PROFILE_SLUG_QUERY, {
      slug,
      organizerProfileId: organizerProfileId || null,
    });
  }

  /** Create an organizer profile. The server derives `slug` from the name when it is absent. */
  static async create(params: OrganizerProfileInput): Promise<OpResult> {
    return ApiService.executeMutation(ORGANIZER_PROFILE_CREATE_MUTATION, {
      params,
    });
  }

  /** Update an organizer profile. */
  static async update(
    where: OrganizerProfileWhereInput,
    params: OrganizerProfileInput
  ): Promise<OpResult> {
    return ApiService.executeMutation(ORGANIZER_PROFILE_UPDATE_MUTATION, {
      where,
      params,
    });
  }

  /** Remove an organizer profile (soft delete). */
  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(ORGANIZER_PROFILE_REMOVE_MUTATION, {
      where: { id },
    });
  }

  /**
   * Remove several profiles. The API has no bulk-remove mutation, so this fans out
   * individual removes and returns the first failure (or the last success).
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    const results = await runBulkWrites(ids, (id) => OrganizerProfileService.remove(id));
    const failed = results.find((r) => r.code !== OP_RESULT_CODES.OK);

    return failed ?? results[results.length - 1] ?? new OpResult();
  }
}
